import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { MANAGEMENT_URL } from "@/config/env";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthCard } from "@/components/AuthCard";

// #343 (UC-9): Einladung des Erst-Admins einlösen. Der Link aus dem Portal trägt E-Mail + Token
// (`/ui/einladung?email=…&token=…`); der Eingeladene setzt hier sein EIGENES Passwort — niemand
// sonst kennt es je. Response = Login-Token (gleiche Form wie /auth/login) → direkt eingeloggt.
// Jeder Fehlschlag am Master ist ein 401 ohne Grund (kein Orakel über E-Mail, Token, Ablauf).
interface InviteResponse {
  accessToken: string;
  expiresAt: string;
  user: { id: string; email: string; role: string };
}

function isValidInviteResponse(value: unknown): value is InviteResponse {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  if (typeof v.accessToken !== "string" || v.accessToken.length === 0) return false;
  if (typeof v.expiresAt !== "string") return false;
  if (!Number.isFinite(new Date(v.expiresAt).getTime())) return false;
  if (typeof v.user !== "object" || v.user === null) return false;
  const u = v.user as Record<string, unknown>;
  return typeof u.id === "string" && typeof u.email === "string" && typeof u.role === "string";
}

export function Invite() {
  const [params] = useSearchParams();
  // Werte EINMAL aus dem Link übernehmen und die Query danach aus Adressleiste + History nehmen (Review LOW-9:
  // Screenshot/Screensharing beim Onboarding, Browser-History). Der Master loggt ohnehin nur den Pfad.
  const [tokenFromLink] = useState(() => params.get("token") ?? "");
  const [email, setEmail] = useState(() => params.get("email") ?? "");
  useEffect(() => {
    if (window.location.search) window.history.replaceState(window.history.state, "", window.location.pathname);
  }, []);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setSession } = useAuth();
  const navigate = useNavigate();
  // Ohne Token gibt es nichts einzulösen — die Seite sagt das, statt ein Formular ins Leere anzubieten.
  const linkUnvollstaendig = useMemo(() => tokenFromLink.length < 16, [tokenFromLink]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Passwort muss mindestens 8 Zeichen haben");
      return;
    }
    if (password !== confirm) {
      setError("Passwörter stimmen nicht überein");
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await fetch(`${MANAGEMENT_URL}/auth/invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, token: tokenFromLink, password }),
      });
      if (res.status === 401) {
        setError("Einladung ungültig, abgelaufen oder bereits eingelöst — bitte eine neue Einladung anfordern.");
        return;
      }
      if (res.status === 400) {
        setError("Ungültige Eingabe — E-Mail-Format und Passwort (min. 8 Zeichen) prüfen.");
        return;
      }
      if (!res.ok) {
        setError(`Einlösen fehlgeschlagen (HTTP ${res.status})`);
        return;
      }
      const data: unknown = await res.json();
      if (!isValidInviteResponse(data)) {
        setError("Antwort-Schema ungültig (Server-Bug?)");
        return;
      }
      setSession({ accessToken: data.accessToken, expiresAt: new Date(data.expiresAt).getTime(), user: data.user });
      navigate("/", { replace: true });
    } catch (err) {
      setError(`Server nicht erreichbar: ${(err as Error).message}`);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthCard title="Einladung annehmen" description="Setze dein Passwort für die Admin-Konsole. Der Link ist einmalig gültig.">
      {linkUnvollstaendig ? (
        <div className="rounded-md border border-danger-border bg-danger-bg px-3 py-2 text-sm text-danger" role="alert" data-testid="invite-missing">
          Dieser Link enthält kein Einladungs-Token. Bitte den vollständigen Link aus der Einladung verwenden.
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="invite-form">
          <div className="space-y-2">
            <Label htmlFor="email">E-Mail</Label>
            <Input id="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={isSubmitting} data-testid="email-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Passwort (min. 8 Zeichen)</Label>
            <Input id="password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} required disabled={isSubmitting} data-testid="password-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm">Passwort bestätigen</Label>
            <Input id="confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required disabled={isSubmitting} data-testid="confirm-input" />
          </div>
          {error && (
            <div className="rounded-md border border-danger-border bg-danger-bg px-3 py-2 text-sm text-danger" role="alert" data-testid="invite-error">
              {error}
            </div>
          )}
          <Button type="submit" className="w-full" disabled={isSubmitting} data-testid="submit-button">
            {isSubmitting ? "Wird gesetzt…" : "Passwort setzen & einloggen"}
          </Button>
        </form>
      )}
    </AuthCard>
  );
}
